/**
 * Initializes WPD Image Manager.
 */
function init(wpd, wpdWindow, wpdDocument, dataEntryVM) {
	class ImageManager {
		#firstLoad = true;
		#newLoad = false;
		#imageInfo = {
			width: 0,
			height: 0,
		};

		saveImage() {
			wpd.graphicsWidget.saveImage();
		}

		load() {
			let $input = wpdDocument.getElementById("fileLoadBox");
			if ($input.files.length > 0) {
				this.#newLoad = true;
				this.initializeFileManager($input.files);
				wpd.appData.setPageManager(null);
				this.loadFromFile($input.files[0]);
			}
			wpd.popup.close("loadNewImage");
		}

		#fileToDataUrl(file) {
			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => {
					resolve(reader.result);
				};
				reader.onerror = reject;
				reader.readAsDataURL(file);
			});
		}

		async #loadImageFile(file, resumedProject) {
			const url = await this.#fileToDataUrl(file);

			await this.loadFromURL(url, resumedProject);
		}

		async #loadPdfFile(file) {
			const url = await this.#fileToDataUrl(file);

			const pdf = await wpdWindow.pdfjsLib.getDocument(url).promise;

			const existingPageManager = wpd.appData.getPageManager();
			if (existingPageManager) {
				await existingPageManager.renderPage(existingPageManager.currentPage());
			} else {
				const pdfManager = this.initializePDFManager(pdf);
				// Register the page manager before the first renderPage so that
				// graphicsWidget.isPdf picks up the PDFManager when the image
				// loads. Otherwise rotateAndResize skips the text-layer refresh
				// on zoom until the user switches pages (which re-enters the
				// image load with the page manager already registered).
				wpd.appData.setPageManager(pdfManager);
				await pdfManager.renderPage(1);
			}
		}

		loadFromFile(file, resumedProject) {
			return new Promise(async (resolve) => {
				wpd.busyNote.show();

				const zoom100Button = wpdDocument.querySelector(
					"button[title='View actual size']",
				);

				if (file.type.match("image.*")) {
					await this.#loadImageFile(file, resumedProject);
					resolve();
				} else if (file.type == "application/pdf") {
					await this.#loadPdfFile(file);
					resolve();
				} else {
					// do not reject the promise to avoid preventing the
					// next file to load
					console.error(`Invalid file type: ${file.type}`);
					wpd.busyNote.close();
					wpd.messagePopup.show(
						wpd.gettext("invalid-file"),
						wpd.gettext("invalid-file-text"),
					);
				}
			});
		}

		loadFromURL(url, isQualityChange) {
			return new Promise((resolve, reject) => {
				let image = new Image();
				image.onload = () => {
					this.#setImage(image, isQualityChange);
					resolve();
				};
				// Without an error path this promise never settles when a decode
				// fails, so busyNote is never closed and the user is left with a
				// spinner over a blank canvas forever. Fail loudly instead.
				image.onerror = () => {
					wpd.busyNote.close();
					reject(new Error("Failed to decode the image for this page."));
				};
				image.src = url;
			});
		}

		// Hand an already-rendered canvas straight to the graphics widget.
		// #setImage only reads .width / .height and passes the source to
		// drawImage, all of which a canvas satisfies, so the PNG encode, the
		// base64 string and the image decode that loadFromURL pays for are pure
		// overhead on the PDF path. loadFromURL stays for real image files,
		// which genuinely arrive as a URL.
		loadFromCanvas(canvas, isQualityChange) {
			this.#setImage(canvas, isQualityChange);
		}

		#setImage(image, isQualityChange) {
			if (this.#newLoad) {
				wpd.appData.reset();
				wpd.sidebar.clear();
			}
			const pageManager = wpd.appData.getPageManager();
			if (pageManager) {
				wpd.graphicsWidget.setRotation(pageManager.getRotation());
			}
			let imageData = wpd.graphicsWidget.loadImage(image, isQualityChange);
			wpd.appData.plotLoaded(imageData);
			wpd.busyNote.close();
			if (this.#newLoad) {
				wpd.tree.refresh();
			} else if (!isQualityChange) {
				wpd.tree.refreshPreservingSelection();
				wpd.sidebar.clear();
			}
			if (this.#firstLoad) {
				wpd.sidebar.show("start-sidebar");
			}
			this.#firstLoad = false;
			this.#newLoad = false;
			this.#imageInfo = {
				width: imageData.width,
				height: imageData.height,
			};
		}

		getImageInfo() {
			return this.#imageInfo;
		}

		initializeFileManager(files, startIndex) {
			const fileManager = wpd.appData.getFileManager();
			fileManager.reset();
			// prevent fileManager from loading unsupported file
			fileManager.currentIndex = startIndex;
			fileManager.set(files);
		}

		initializePDFManager(pdf, skipInputRefresh) {
			const pdfManager = new wpd.PDFManager();
			pdfManager.init(pdf, skipInputRefresh);
			return pdfManager;
		}
	}

	wpd.imageManager = new ImageManager();
}

export { init };
